/**
 * Canonical KrossKast screensaver idle-display policy (SA mirror).
 *
 * Must stay aligned with Assistant `SystemDreamPolicy.IDLE_TIMEOUT_MS`.
 * Structured for future Fleet/device field consumption — do not scatter
 * hard-coded "1 minute" strings independent of this policy object.
 */
export type ScreensaverIdlePolicy = {
  /** Idle timeout in milliseconds (matches device screen_off_timeout). */
  idleTimeoutMs: number;
  /** Human label for Screen Timeout row. */
  screenTimeoutLabel: string;
  /** Idle display when screensaver (animated Dream) is disabled. */
  disabledIdleDisplayLabel: string;
};

/** Default / current shipping policy. */
export const SCREENSAVER_IDLE_POLICY: ScreensaverIdlePolicy = {
  idleTimeoutMs: 60_000,
  screenTimeoutLabel: "After 1 minute of inactivity",
  disabledIdleDisplayLabel: "Black Screen",
};

/**
 * Resolve policy for UI. Prefer device/Fleet-provided timeout when present;
 * otherwise fall back to the canonical constant.
 */
export function resolveScreensaverIdlePolicy(args?: {
  idleTimeoutMs?: number | null;
}): ScreensaverIdlePolicy {
  const ms =
    typeof args?.idleTimeoutMs === "number" &&
    Number.isFinite(args.idleTimeoutMs) &&
    args.idleTimeoutMs > 0
      ? args.idleTimeoutMs
      : SCREENSAVER_IDLE_POLICY.idleTimeoutMs;

  if (ms === SCREENSAVER_IDLE_POLICY.idleTimeoutMs) {
    return SCREENSAVER_IDLE_POLICY;
  }

  const minutes = ms / 60_000;
  const screenTimeoutLabel =
    Number.isInteger(minutes) && minutes >= 1
      ? `After ${minutes} minute${minutes === 1 ? "" : "s"} of inactivity`
      : `After ${ms} ms of inactivity`;

  return {
    idleTimeoutMs: ms,
    screenTimeoutLabel,
    disabledIdleDisplayLabel: SCREENSAVER_IDLE_POLICY.disabledIdleDisplayLabel,
  };
}

export function formatScreensaverEnabledLabel(
  enabled: boolean | null | undefined
): string {
  if (enabled === true) return "Enabled";
  if (enabled === false) return "Disabled";
  return "Unknown";
}
